import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import options from "./js/options.js"
import light from "./js/light.js"
import loadingManager from "./js/loader.js"
import { pdfCreate } from "./js/pdf.js"
import { EffectComposer, EXRLoader, RenderPass, SSAOPass, SMAAPass, OutputPass } from "three/addons"
import theme from "./js/theme.js"
import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min.js'
import {initAIImageGenerator} from "./js/ai.js";

// ─── СЦЕНА ───────────────────────────────────

const canvasContainer = document.querySelector('#canvas-container')
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
const isLarge = canvasContainer.getBoundingClientRect().width > 600

const sizes = {
    width: canvasContainer.getBoundingClientRect().width,
    height: canvasContainer.getBoundingClientRect().height
}

// ─── PIXEL RATIO (вынесен глобально для переиспользования) ────────────────────

const pixelRatio = Math.min(window.devicePixelRatio, 2)

// ─── ЗАГРУЗЧИКИ ───────────────────────────────

const dracoLoader = new DRACOLoader(loadingManager)
dracoLoader.setDecoderPath('./draco/')
const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)

// ─── КАМЕРА ───────────────────────────────────

const camera = new THREE.PerspectiveCamera(38, sizes.width / sizes.height, 0.3, 100)
isLarge ? camera.position.set(7, 6, 9) : camera.position.set(8, 8, 12)
scene.add(camera)

// ─── КОНТРОЛЫ ─────────────────────────────────

const controls = new OrbitControls(camera, canvas)
controls.target.set(0, 0.75, 0)
controls.enableDamping = true
controls.dampingFactor = 0.04
controls.enablePan = false
controls.minDistance = 1
controls.maxDistance = isLarge ? 5 : 11
controls.maxPolarAngle = Math.PI / 2
controls.minAzimuthAngle = -0.3
controls.maxAzimuthAngle = Math.PI / 3

// ─── РЕНДЕРЕР ─────────────────────────────────

const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    preserveDrawingBuffer: true,
    alpha: false,
    powerPreference: 'high-performance'
})

renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(pixelRatio)
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.setClearColor('#f0ede8')
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 0.95
renderer.outputColorSpace = THREE.SRGBColorSpace

// ─── HDR ENVIRONMENT ──────────────────────────

const pmremGenerator = new THREE.PMREMGenerator(renderer)
pmremGenerator.compileEquirectangularShader()

new EXRLoader().load('./models/brown_photostudio_04_2k.exr', (texture) => {
    texture.mapping = THREE.EquirectangularReflectionMapping
    const envMap = pmremGenerator.fromEquirectangular(texture).texture
    scene.environment = envMap
    scene.environmentIntensity = 0.85
    texture.dispose()
    pmremGenerator.dispose()
})

// ─── ОСВЕЩЕНИЕ ────────────────────────────────

light(scene)

const keyLight = new THREE.DirectionalLight(0xfff8f0, 1.1)
keyLight.position.set(5, 9, 6)
keyLight.castShadow = true
keyLight.shadow.mapSize.width = 4096
keyLight.shadow.mapSize.height = 4096
keyLight.shadow.camera.near = 0.5
keyLight.shadow.camera.far = 30
keyLight.shadow.camera.left = -6
keyLight.shadow.camera.right = 6
keyLight.shadow.camera.top = 6
keyLight.shadow.camera.bottom = -6
keyLight.shadow.bias = -0.0003
keyLight.shadow.normalBias = 0.015
keyLight.shadow.radius = 2
scene.add(keyLight)

const rimLight = new THREE.DirectionalLight(0xddeeff, 0.4)
rimLight.position.set(-6, 4, -3)
scene.add(rimLight)

const fillLight = new THREE.DirectionalLight(0xfff5e8, 0.35)
fillLight.position.set(2, -1, 8)
scene.add(fillLight)

const ambientLight = new THREE.AmbientLight(0xfff0e8, 0.25)
scene.add(ambientLight)

const shadowPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(20, 20),
    new THREE.ShadowMaterial({ opacity: 0.20 })
)
shadowPlane.rotation.x = -Math.PI / 2
shadowPlane.position.y = -0.001
shadowPlane.receiveShadow = true
scene.add(shadowPlane)

// ─── POST-PROCESSING ──────────────────────────

// ✅ FIX: RenderTarget создаётся с учётом pixelRatio — иначе в Chrome на Retina размыто
const renderTarget = new THREE.WebGLRenderTarget(
    sizes.width * pixelRatio,
    sizes.height * pixelRatio,
    {
        samples: 16,
        type: THREE.HalfFloatType
    }
)

const composer = new EffectComposer(renderer, renderTarget)
composer.addPass(new RenderPass(scene, camera))

// ✅ FIX: SSAOPass тоже с учётом pixelRatio
const ssaoPass = new SSAOPass(scene, camera, sizes.width * pixelRatio, sizes.height * pixelRatio)
ssaoPass.kernelRadius = 0.28
ssaoPass.minDistance = 0.0001
ssaoPass.maxDistance = 0.1
ssaoPass.output = SSAOPass.OUTPUT.Default
composer.addPass(ssaoPass)

composer.addPass(new SMAAPass(
    sizes.width * pixelRatio,
    sizes.height * pixelRatio
))
composer.addPass(new OutputPass())

// ─── ФОН ──────────────────────────────────────

const backgroundMaterial = new THREE.ShaderMaterial({
    depthTest: true,
    depthWrite: false,
    uniforms: {
        centerColor: { value: new THREE.Color(0xf5f1eb) },
        edgeColor:   { value: new THREE.Color(0xd8d0c4) },
        uAspect:     { value: sizes.width / sizes.height }
    },
    vertexShader: `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = vec4(position.xy, 0.9999, 1.0);
        }`,
    fragmentShader: `
        varying vec2 vUv;
        uniform vec3 centerColor;
        uniform vec3 edgeColor;
        uniform float uAspect;
        float random(vec2 c) {
            return fract(sin(dot(c, vec2(12.9898, 78.233))) * 43758.5453);
        }
        void main() {
            vec2 uv = vUv;
            uv.x = (vUv.x - 0.5) * uAspect + 0.5;
            float factor = smoothstep(0.1, 0.9, distance(uv, vec2(0.5, 0.45)));
            vec3 color = mix(centerColor, edgeColor, factor * 0.7);
            float noise = (random(vUv) - 0.5) * (1.5 / 255.0);
            gl_FragColor = vec4(color + noise, 1.0);
        }`
})

const backgroundMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), backgroundMaterial)
backgroundMesh.frustumCulled = false
backgroundMesh.position.z = -50
scene.add(backgroundMesh)

theme(scene, backgroundMaterial)

// ─── МОДЕЛЬ ───────────────────────────────────

const sceneSettings = {
    panelColor: '#d6d1c8',
    wallColor: '#e5e2dc'
}

let panelMaterials = []
let wallMaterial = null

let model = null
let originalMaterial = null

gltfLoader.load('./models/KitchenBlackWood.glb', async (gltf) => {
    model = gltf.scene

    model.traverse((child) => {
        if (child.name.toLowerCase().includes('straight')) {
            child.visible = false
            child.castShadow = false
        }
        if (child.name.toLowerCase().includes('bar')) {
            child.visible = false
            child.castShadow = false
        }

        if (child.isMesh) {
            child.castShadow = child.visible
            child.receiveShadow = true

            const materials = Array.isArray(child.material) ? child.material : [child.material]

            materials.forEach(mat => {
                const matName = mat.name.toLowerCase()
                const objName = child.name.toLowerCase()

                if (matName.includes('panelblack')) {
                    mat.color.set(sceneSettings.panelColor)
                    if (!panelMaterials.includes(mat)) panelMaterials.push(mat)
                }

                if (objName === 'wall' || matName === 'wall') {
                    wallMaterial = mat
                    wallMaterial.color.set(sceneSettings.wallColor)
                }
            })

            if (child.material.name === 'woodPanelMiddle') {
                originalMaterial = child.material
            }
        }
    })

    model.position.y = 0
    scene.add(model)

    options(model, originalMaterial, scene, camera)
    await pdfCreate(renderer, camera, scene, model, backgroundMaterial)

    initAIImageGenerator({
        renderer,
        camera,
        scene,
    });
})

// ─── RESIZE ───────────────────────────────────

window.addEventListener('resize', () => {
    sizes.width = canvasContainer.clientWidth
    sizes.height = canvasContainer.clientHeight

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    const currentPixelRatio = Math.min(window.devicePixelRatio, 2)

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(currentPixelRatio)

    // ✅ FIX: обновляем renderTarget с учётом нового pixelRatio
    renderTarget.setSize(sizes.width * currentPixelRatio, sizes.height * currentPixelRatio)

    composer.setSize(sizes.width, sizes.height)
    composer.setPixelRatio(currentPixelRatio)

    backgroundMaterial.uniforms.uAspect.value = sizes.width / sizes.height
})

// ─── АНИМАЦИЯ ─────────────────────────────────

const tick = () => {
    controls.update()
    composer.render()
    window.requestAnimationFrame(tick)
}

tick()